import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import { Bot, Power, AlertTriangle, RefreshCw, BarChart3, MessageSquare, LifeBuoy, Database, Save } from "lucide-react";

export default function AdminYeda() {
  const c = adminClient();
  const [tab, setTab] = useState("config");
  const [cfg, setCfg] = useState(null); const [index, setIndex] = useState(null); const [configured, setConfigured] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [convos, setConvos] = useState([]); const [escs, setEscs] = useState([]); const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await c.get("/admin/yeda/config");
    setCfg(data.config); setIndex(data.index); setConfigured(data.configured);
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "analytics") c.get("/admin/yeda/analytics").then((r) => setAnalytics(r.data));
    if (tab === "convos") c.get("/admin/yeda/conversations").then((r) => setConvos(r.data.conversations));
    if (tab === "escalations") c.get("/admin/yeda/escalations").then((r) => setEscs(r.data.escalations));
    if (tab === "sources") c.get("/admin/yeda/sources").then((r) => setSources(r.data));
  }, [tab]); // eslint-disable-line

  const save = async (patch) => { setBusy(true); try { await c.put("/admin/yeda/config", patch); await load(); } finally { setBusy(false); } };

  if (!cfg) return <div className="h-40 bg-black/5 animate-pulse" />;
  const TABS = [["config", "Configuration"], ["analytics", "Analytics"], ["convos", "Conversations"], ["escalations", "Escalations"], ["sources", "Sources"]];

  return (
    <div data-testid="admin-yeda">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-black font-display flex items-center gap-2"><Bot className="w-5 h-5 text-taven-gold" /> YEDA Business Guide Center</h2>
        <div className="flex gap-1 flex-wrap">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-xs font-bold uppercase ${tab === k ? "bg-taven-black text-white" : "border border-black/15"}`} data-testid={`yeda-tab-${k}`}>{l}</button>)}</div>
      </div>

      {!configured && <div className="border border-red-300 bg-red-50 p-3 mb-4 text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Provider not configured — YEDA fails closed and will not answer policy questions.</div>}

      {tab === "config" && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="yeda-config">
          <div className="border border-black/10 bg-white p-4">
            <h3 className="font-black font-display uppercase text-xs tracking-wide mb-3">Availability</h3>
            <Toggle label="Enabled" v={cfg.enabled} onChange={(v) => save({ enabled: v })} testid="yeda-enabled" />
            <Toggle label="Emergency Kill Switch" v={cfg.kill_switch} danger onChange={(v) => save({ kill_switch: v })} testid="yeda-kill" />
            <label className="block mt-3 text-xs font-bold uppercase tracking-wide text-black/60">Model
              <select value={cfg.model} onChange={(e) => save({ model: e.target.value })} className="block mt-1 border border-black/15 px-3 py-2 text-sm" data-testid="yeda-model">
                {["gpt-5.4", "gpt-4o", "gpt-4o-mini"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <p className="text-[11px] text-black/40 mt-2">Provider: {cfg.provider} (server-side key)</p>
          </div>
          <div className="border border-black/10 bg-white p-4">
            <h3 className="font-black font-display uppercase text-xs tracking-wide mb-3">Per-Program Guidance</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(cfg.programs || {}).map(([p, on]) => (
                <Toggle key={p} label={p} v={on} onChange={(v) => save({ programs: { ...cfg.programs, [p]: v } })} testid={`yeda-prog-${p}`} />
              ))}
            </div>
          </div>
          {index && <div className="border border-black/10 bg-white p-4 md:col-span-2 text-sm flex gap-6" data-testid="yeda-index">
            <span><Database className="w-4 h-4 inline text-taven-gold" /> Published legal: <strong>{index.legal_published}</strong></span>
            <span>Unpublished legal: <strong>{index.legal_unpublished}</strong></span>
            <span>Program sources: <strong>{index.program_sources}</strong></span>
          </div>}
        </div>
      )}

      {tab === "analytics" && analytics && (
        <div data-testid="yeda-analytics">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Conversations" v={analytics.total} />
            <Stat label="Escalation Rate" v={`${analytics.escalation_rate_pct}%`} />
            <Stat label="Refusal Rate" v={`${analytics.refusal_rate_pct}%`} />
            <Stat label="Coverage Gaps" v={analytics.coverage_gaps} />
          </div>
          <div className="border border-black/10 bg-white p-4 mb-4 text-xs" data-testid="yeda-bylang">By language: {Object.entries(analytics.by_language || {}).map(([l, n]) => <span key={l} className="mr-3">{l}: <strong>{n}</strong></span>)}</div>
          <div className="border border-black/10 bg-white p-4">
            <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2 flex items-center gap-1"><BarChart3 className="w-4 h-4" /> Most Common Questions</h3>
            {(analytics.top_questions || []).map((t, i) => <div key={i} className="text-sm py-1 border-b border-black/5 flex justify-between"><span>{t.q}</span><span className="text-black/40">{t.count}</span></div>)}
          </div>
        </div>
      )}

      {tab === "convos" && (
        <div className="space-y-2" data-testid="yeda-convos">
          {convos.map((m) => (
            <div key={m.id} className="border border-black/10 bg-white p-3 text-sm">
              <div className="flex justify-between text-xs text-black/40"><span>{m.language?.toUpperCase()} • {m.confidence} • {m.escalated ? "escalated" : (m.refusal_reason || "answered")}</span><span>{new Date(m.at).toLocaleString()}</span></div>
              <p className="font-bold mt-1">Q: {m.question}</p>
              <p className="text-black/70 mt-1">A: {m.answer}</p>
              {m.source_ids?.length > 0 && <p className="text-[11px] text-black/40 mt-1">Sources: {m.source_ids.join(", ")} (v{(m.source_versions || []).join(", ")})</p>}
            </div>
          ))}
          {convos.length === 0 && <p className="text-black/40 text-sm">No conversations yet.</p>}
        </div>
      )}

      {tab === "escalations" && (
        <div className="space-y-2" data-testid="yeda-escalations">
          {escs.map((e) => (
            <div key={e.id} className="border border-orange-200 bg-orange-50 p-3 text-sm">
              <div className="flex justify-between text-xs text-orange-700"><span><LifeBuoy className="w-3.5 h-3.5 inline" /> {e.reason} • {e.priority} • {e.status}</span><span>{new Date(e.created_at).toLocaleString()}</span></div>
              <p className="font-bold mt-1">{e.question}</p>
              <p className="text-black/60 mt-0.5">{e.yeda_response}</p>
              <p className="text-[11px] text-black/40 mt-1">Queue: {e.assigned_team} • Lang: {e.language} {e.contact ? `• ${e.contact}` : ""}</p>
            </div>
          ))}
          {escs.length === 0 && <p className="text-black/40 text-sm">No escalations.</p>}
        </div>
      )}

      {tab === "sources" && sources && (
        <div data-testid="yeda-sources-admin">
          <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2">Legal Sources (published are used in production)</h3>
          <div className="border border-black/10 overflow-x-auto mb-4"><table className="w-full text-sm"><thead className="bg-taven-gray/50 text-left text-xs uppercase"><tr><th className="p-2">Document</th><th className="p-2">Status</th></tr></thead>
            <tbody>{sources.legal.map((d) => <tr key={d.key} className="border-t border-black/5"><td className="p-2">{d.title}</td><td className="p-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${d.status === "published" ? "bg-green-100 text-green-700" : "bg-black/10 text-black/50"}`}>{d.status}</span></td></tr>)}</tbody></table></div>
          <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2">Program Sources (always approved)</h3>
          <ul className="text-sm list-disc pl-5">{sources.program_sources.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

const Toggle = ({ label, v, onChange, danger, testid }) => (
  <label className="flex items-center justify-between py-1.5 text-sm capitalize">
    <span className={danger ? "text-red-700 font-bold" : ""}>{label}</span>
    <button onClick={() => onChange(!v)} className={`w-11 h-6 rounded-full relative transition-colors ${v ? (danger ? "bg-red-600" : "bg-green-600") : "bg-black/20"}`} data-testid={testid}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${v ? "left-5" : "left-0.5"}`} />
    </button>
  </label>
);
const Stat = ({ label, v }) => (<div className="border border-black/10 bg-white p-3 text-center"><p className="text-2xl font-black font-display">{v}</p><p className="text-[10px] uppercase tracking-wide text-black/40">{label}</p></div>);
